import type {
  InterviewInsights,
  ParticipantStudyView,
  TranscriptTurn,
} from "@leaply-interview/shared-types";

/**
 * Thin typed client for the NestJS api. Wraps fetch with:
 *   - VITE_API_URL base (defaults to "/api" — proxied in dev)
 *   - JSON Content-Type
 *   - Error parsing (extracts NestJS `{error: "..."}` strings into a typed
 *     thrown ApiError the caller can branch on)
 *
 * No retries, no caching — kept dumb so the calling pages own behaviour. If
 * we need TanStack Query later, add it at the page level.
 */

const BASE_URL: string = import.meta.env.VITE_API_URL ?? "/api";

/** Thrown for any non-2xx response. `status` is the HTTP status, `code` is the
 *  NestJS `error` field when the body matches `{error: "..."}` (e.g.
 *  `already_completed`, `study_closed`, `transcript_too_short`). Pages branch
 *  on `code` to route correctly. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const finalHeaders = new Headers(headers);
  if (json !== undefined) {
    finalHeaders.set("Content-Type", "application/json");
  }
  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (!res.ok) {
    let code: string | null = null;
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: unknown; message?: unknown };
      if (typeof body.error === "string") {
        code = body.error;
        message = body.error;
      } else if (typeof body.message === "string") {
        message = body.message;
      }
    } catch {
      /* body wasn't json — keep status-line message */
    }
    throw new ApiError(message, res.status, code);
  }

  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

// ── Realtime sessions ───────────────────────────────────────────────────────

export interface RealtimeSessionResponse {
  client_secret: { value: string };
  expires_at?: number;
  model: string;
  voice: string;
  mock?: true;
}

export function mintRealtimeSession(
  studyId: string,
): Promise<RealtimeSessionResponse> {
  return request("/realtime-sessions", {
    method: "POST",
    json: { studyId },
  });
}

// ── Interviews ──────────────────────────────────────────────────────────────

export interface InitiateResponse {
  interviewId: string;
  /** Participant's Leaply (identity) user id — the Amplitude user_id.
   *  Undefined when they're not in identity.users; we then send nothing to
   *  Amplitude. Never the raw email. */
  userId?: string;
  study: ParticipantStudyView;
}

export function initiateInterview(
  email: string,
  studyId: string,
  deviceId?: string,
): Promise<InitiateResponse> {
  return request("/interviews/initiate", {
    method: "POST",
    json: { email, studyId, ...(deviceId ? { deviceId } : {}) },
  });
}

export interface FinishInterviewBody {
  transcript: TranscriptTurn[];
  /** ISO timestamp of when the call actually connected (NOT when the link was
   *  opened). The server clamps the reported duration to this window. */
  startedAt?: string;
  endedAt?: string;
  /** Active call seconds (pause time excluded), measured client-side. The
   *  server clamps it to the real window + the study cap. */
  durationS?: number;
  audioGcsPath?: string;
  usage?: Record<string, unknown>;
}

/** Subset of the finished interview we read client-side. `status` decides
 *  reward eligibility on /complete (completed_invalid → no reward). */
export interface FinishInterviewResult {
  status?: string;
  invalidReason?: string | null;
}

export function finishInterview(
  interviewId: string,
  body: FinishInterviewBody,
): Promise<FinishInterviewResult> {
  return request(`/interviews/${interviewId}`, {
    method: "PATCH",
    json: body,
  });
}

export interface AudioUploadUrl {
  uploadUrl: string;
  gcsPath: string;
  contentType: string;
  expiresAt: number;
}

export function mintAudioUploadUrl(
  interviewId: string,
  contentType?: string,
): Promise<AudioUploadUrl> {
  return request(`/interviews/${interviewId}/audio-url`, {
    method: "POST",
    ...(contentType ? { json: { contentType } } : {}),
  });
}

export function generateInsights(
  interviewId: string,
): Promise<{ insights: InterviewInsights; cached?: true }> {
  return request(`/interviews/${interviewId}/insights`, { method: "POST" });
}
